import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function RealisationDetail() {
    const { slug } = useParams();
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setLoading(true);
        api.get(`/projects/${slug}`)
            .then(({ data }) => {
                setProject(data);
                document.title = `${data.title} — Japet Agencement`;
            })
            .catch((e) => setError(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [slug]);

    if (loading) return <div className="max-w-3xl mx-auto px-6 py-32 text-brand-sageDark">Chargement…</div>;
    if (error || !project) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-32">
                <h1 className="font-serif text-4xl text-brand-ink">Projet introuvable</h1>
                <Link to="/realisations" className="mt-6 inline-block text-brand-sage underline">← Toutes les réalisations</Link>
            </div>
        );
    }

    return (
        <article className="max-w-6xl mx-auto px-6 md:px-12 py-16" data-testid="project-page">
            <Link to="/realisations" data-testid="project-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-brand-sage">
                <ArrowLeft size={14} /> Toutes les réalisations
            </Link>

            <header className="mt-10">
                {project.category && <span className="overline">{project.category}</span>}
                <h1 className="mt-4 font-serif text-5xl md:text-7xl font-light text-brand-ink leading-[1.05]">{project.title}</h1>
                <p className="mt-4 text-sm uppercase tracking-[0.22em] text-brand-sage">
                    {[project.location, project.surface, project.year].filter(Boolean).join(" · ")}
                </p>
                {project.summary && (
                    <p className="mt-6 text-xl text-brand-sageDark font-light leading-relaxed max-w-3xl">{project.summary}</p>
                )}
            </header>

            {project.before_image && project.after_image && (
                <div className="mt-12 grid md:grid-cols-2 gap-6">
                    <div>
                        <p className="overline mb-3">Avant</p>
                        <img src={project.before_image} alt="Avant rénovation" className="w-full aspect-[4/3] object-cover" />
                    </div>
                    <div>
                        <p className="overline mb-3 text-brand-sage">Après</p>
                        <img src={project.after_image} alt="Après rénovation" className="w-full aspect-[4/3] object-cover" />
                    </div>
                </div>
            )}

            {!project.before_image && project.after_image && (
                <img src={project.after_image} alt={project.title} className="mt-12 w-full aspect-[16/9] object-cover" />
            )}

            {project.description && (
                <div className="prose-japet mt-16 max-w-3xl mx-auto" dangerouslySetInnerHTML={{ __html: project.description }} />
            )}

            {project.gallery?.length > 0 && (
                <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {project.gallery.map((g, i) => (
                        <img key={i} src={g} alt={`${project.title} ${i + 1}`} className="aspect-[4/3] object-cover" />
                    ))}
                </div>
            )}

            {project.client_quote && (
                <blockquote className="mt-20 max-w-3xl mx-auto text-center">
                    <p className="font-serif text-3xl md:text-4xl italic font-light text-brand-ink leading-tight">
                        « {project.client_quote} »
                    </p>
                </blockquote>
            )}

            <div className="mt-20 text-center">
                <Link to="/devis" className="inline-flex items-center gap-3 px-9 py-5 bg-brand-ink hover:bg-brand-sage text-brand-bg text-sm uppercase tracking-[0.22em] transition-colors">
                    Confier votre projet
                </Link>
            </div>
        </article>
    );
}
